import {
  effect,
  Injectable,
  type Injector,
  isSignal,
  linkedSignal,
  type Signal,
  signal,
  untracked,
  type WritableSignal,
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { isEqual } from 'lodash-es';
import { EMPTY, filter, fromEvent, map, merge, type Observable, of, startWith, Subject, switchMap } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class LocalStorageService {
  private readonly keyPrefix = 'reconcept-portfolio';

  // only fires for changes made in other tabs; a null key means the full storage is cleared
  private readonly otherTabKeyChanges = fromEvent<StorageEvent>(window, 'storage').pipe(map((event) => event.key));
  // only fires for changes made in the current
  private readonly localKeyChanges = new Subject<string>();

  // unchanged values are skipped, so no change is emitted for them
  clear(key: string) {
    key = this.prefixedKey(key);

    if (localStorage.getItem(key) !== null) {
      localStorage.removeItem(key);
      this.localKeyChanges.next(key);
    }
  }

  store(key: string, value: any) {
    key = this.prefixedKey(key);

    // we always stringify the value, since the localstorage only works with strings
    const storedString = JSON.stringify(value);

    if (localStorage.getItem(key) !== storedString) {
      localStorage.setItem(key, storedString);
      this.localKeyChanges.next(key);
    }

    return value;
  }

  retrieve(key: string) {
    try {
      // we parse the item from storage, so we get actual booleans for 'false' or 'true'

      const value = localStorage.getItem(this.prefixedKey(key));

      if (value) {
        return JSON.parse(value);
      }
    } catch (e) {
      console.error(e);
    }

    return null;
  }

  // parsed value of this key after every change; null when it is removed
  observe<T>(key: string, options: { onlyOtherTabs?: boolean }): Observable<T | null> {
    const fullKey = this.prefixedKey(key);

    const changedKeys$ = options?.onlyOtherTabs
      ? this.otherTabKeyChanges
      : merge(this.otherTabKeyChanges, this.localKeyChanges);

    return changedKeys$.pipe(
      // when there is no key, the full storage is cleared
      filter((changedKey) => !changedKey || changedKey === fullKey),
      map(() => this.retrieve(key)),
    );
  }

  // signal synced both ways with storage, including changes from other tabs; the default value (null if omitted)
  // is not stored, an undefined key keeps the default and stores nothing
  storedSignal<T>(
    key: string | Signal<string | undefined>,
    options: { defaultValue: T; injector?: Injector },
  ): WritableSignal<T>;
  storedSignal<T>(
    key: string | Signal<string | undefined>,
    options?: { injector?: Injector },
  ): WritableSignal<T | null>;
  storedSignal<T>(
    key: string | Signal<string | undefined>,
    options?: { defaultValue?: T; injector?: Injector },
  ): WritableSignal<T | null> {
    const defaultValue = options?.defaultValue ?? null;
    const keySignal = isSignal(key) ? key : signal(key);
    const readStoredValue = (currentKey: string | undefined): T | null =>
      currentKey ? (this.retrieve(currentKey) ?? defaultValue) : defaultValue;

    const storedValue = toSignal(
      toObservable(keySignal, { injector: options?.injector }).pipe(
        switchMap((currentKey) => {
          if (!currentKey) {
            return of(defaultValue);
          }

          return this.observe<T>(currentKey, { onlyOtherTabs: false }).pipe(
            map((storedValue) => storedValue ?? defaultValue),
            startWith(readStoredValue(currentKey)),
          );
        }),
      ),
      {
        initialValue: readStoredValue(untracked(keySignal)),
        // together with store/clear skipping unchanged values, this prevents a write → change → write loop
        equal: (a, b) => isEqual(a, b),
        injector: options?.injector,
      },
    );

    // writable, reset whenever the stored value changes
    const value = linkedSignal(() => storedValue());

    effect(
      () => {
        const currentKey = keySignal();
        const currentValue = value();
        if (!currentKey) {
          return;
        }

        // when the currentvalue === defaultvalue, the key can be cleared
        if (isEqual(currentValue, defaultValue)) {
          this.clear(currentKey);
        } else {
          this.store(currentKey, currentValue);
        }
      },
      { injector: options?.injector },
    );

    return value;
  }

  private prefixedKey(key: string) {
    return `${this.keyPrefix}|${key}`;
  }
}

// localStorage is not available in SSR
@Injectable({ providedIn: 'root' })
export class SsrLocalStorageService {
  clear(): void {}
  store(): void {}
  retrieve(): any {
    return null;
  }
  observe(): Observable<any> {
    return EMPTY;
  }
  storedSignal<T>(
    _key: string | Signal<string | undefined>,
    options?: { defaultValue?: T; injector?: Injector },
  ): WritableSignal<T | null> {
    return signal(options?.defaultValue ?? null);
  }
}
